import { apiUrl } from '../utils/api-url';

/**
 * Marp slide-deck artefact attached to a project. Stored in the shared `documents`
 * Cosmos container alongside MethodologyDocument; distinguished server-side by
 * `type: "presentation"`. The frontend doesn't need the type field directly — the
 * dedicated `/api/presentations` endpoints already filter to presentations only.
 */
export interface PresentationDocument {
  id?: string;
  customerId: string;
  projectId: string;
  templateId: string;
  title: string;
  cafPhase: string;
  /** 1-based ordinal for multi-session series (Session 1 of 2). Null for single decks. */
  sessionNumber?: number | null;
  status: string;
  version: string;
  /** Marp source markdown — frontmatter declares marp: true, slides separated by `---`. */
  markdownContent: string;
  slideCount?: number | null;
  /** UTC of the last successful PDF render. Null until first render. */
  lastRenderedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export async function listPresentations(
  customerId: string,
  projectId: string,
  token: string,
): Promise<PresentationDocument[]> {
  const res = await fetch(
    apiUrl(`/api/presentations?customerId=${encodeURIComponent(customerId)}&projectId=${encodeURIComponent(projectId)}`),
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) throw new Error(`Failed to list presentations: ${res.status}`);
  return res.json();
}

export async function getPresentation(
  id: string,
  customerId: string,
  token: string,
): Promise<PresentationDocument> {
  const res = await fetch(
    apiUrl(`/api/presentations/${id}?customerId=${encodeURIComponent(customerId)}`),
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) throw new Error(`Failed to get presentation: ${res.status}`);
  return res.json();
}

/**
 * Build a same-origin URL to the render endpoint. Used as the `src` of a
 * `<iframe>` so the browser fetches the PDF directly (with the bearer header
 * attached via a Range/Accept negotiation handled by the existing auth wrap).
 *
 * Callers needing the rendered bytes directly can call this with a fetch +
 * the Authorization header and read the blob.
 */
export function presentationRenderUrl(id: string, customerId: string): string {
  return apiUrl(`/api/presentations/${id}/render?customerId=${encodeURIComponent(customerId)}`);
}

/**
 * Render a presentation to PDF and return the bytes as an object URL the
 * browser can use directly. The caller is responsible for revoking the URL
 * when no longer needed (URL.revokeObjectURL).
 */
export async function renderPresentationToObjectUrl(
  id: string,
  customerId: string,
  token: string,
): Promise<string> {
  const res = await fetch(presentationRenderUrl(id, customerId), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to render presentation: ${res.status}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}
